import type { Message, ToolCall } from '@/types/conversation'

// The backend injects this system message before the first WhatsApp turn of a conversation
const WHATSAPP_CONTEXT_MARKER = '[WHATSAPP CONTEXT]'
const CALL_ENDED = 'The call has ended'
const SEND_WHATSAPP_TOOL = 'send_whatsapp'
const SEND_WHATSAPP_SUCCESS = 'The message has been sent'

/** `index` is the position of the source message in the stored messages, which reviews address turns by */
export type TranscriptLine =
  | { role: 'assistant' | 'user' | 'system'; content: string; index: number }
  | { role: 'tool_call'; toolCall: ToolCall; response?: string | null; index: number }
  | { role: 'whatsapp_sent'; content: string; toolCallId: string; index: number }

export interface SplitTranscript {
  phone: TranscriptLine[]
  whatsapp: TranscriptLine[]
}

function parseMessages(messages: Message[] | string | undefined): Message[] {
  if (!messages) return []
  if (Array.isArray(messages)) return messages
  try {
    const parsed = JSON.parse(messages)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function spokenContent(content: string): string {
  const endIndex = content.indexOf(CALL_ENDED)
  return (endIndex === -1 ? content : content.slice(0, endIndex + CALL_ENDED.length)).trim()
}

function hasVisibleContent(content: string): boolean {
  return content !== '' && content !== "''" && content !== '""'
}

function toLines(messages: Message[], offset: number): TranscriptLine[] {
  const responses = new Map<string, string | null>()
  for (const message of messages) {
    if (message?.role === 'tool' && message.tool_call_id) {
      responses.set(message.tool_call_id, message.content)
    }
  }

  return messages.flatMap((message, position): TranscriptLine[] => {
    const index = offset + position
    // The first stored message is the system prompt. Later system messages
    // (hang-up, transfer, integration context) stay in the transcript.
    if (message?.role === 'system') {
      if (index === 0 || typeof message.content !== 'string') return []
      const content = message.content.trim()
      if (!hasVisibleContent(content) || content.startsWith(WHATSAPP_CONTEXT_MARKER)) return []
      return [{ role: 'system', content, index }]
    }

    if (message?.role !== 'assistant' && message?.role !== 'user') return []
    const lines: TranscriptLine[] = []

    if (typeof message.content === 'string') {
      const content = spokenContent(message.content)

      if (hasVisibleContent(content)) {
        lines.push({ role: message.role, content, index })
      }
    }

    for (const toolCall of message.tool_calls ?? []) {
      lines.push({ role: 'tool_call', toolCall, response: responses.get(toolCall.id), index })
    }
    return lines
  })
}

/** WhatsApp messages the assistant sent during the call, which the transcript only holds as tool calls */
function sentDuringCall(phone: TranscriptLine[]): TranscriptLine[] {
  return phone.flatMap((line): TranscriptLine[] => {
    if (line.role !== 'tool_call' || line.toolCall.function.name !== SEND_WHATSAPP_TOOL) return []
    if (!line.response?.startsWith(SEND_WHATSAPP_SUCCESS)) return []
    try {
      const { body } = JSON.parse(line.toolCall.function.arguments)
      if (typeof body !== 'string' || !body.trim()) return []
      return [
        {
          role: 'whatsapp_sent',
          content: body.trim(),
          toolCallId: line.toolCall.id,
          index: line.index,
        },
      ]
    } catch {
      return []
    }
  })
}

/**
 * Splits the stored conversation messages into the phone call and the WhatsApp chat.
 * The opening system prompt is left out; later system messages stay in their channel.
 * Tool calls are included with their tool result.
 * WhatsApp messages sent during the call lead the WhatsApp chat.
 */
export function splitTranscript(
  messages: Message[] | string | undefined,
  isWhatsAppOrigin: boolean
): SplitTranscript {
  const parsed = parseMessages(messages)
  const markerIndex = parsed.findIndex(
    (message) =>
      message?.role === 'system' &&
      typeof message.content === 'string' &&
      message.content.startsWith(WHATSAPP_CONTEXT_MARKER)
  )

  if (isWhatsAppOrigin) {
    return {
      phone: [],
      whatsapp:
        markerIndex === -1
          ? toLines(parsed, 0)
          : toLines(parsed.slice(markerIndex + 1), markerIndex + 1),
    }
  }

  if (markerIndex === -1) {
    const phone = toLines(parsed, 0)
    return { phone, whatsapp: sentDuringCall(phone) }
  }

  const phone = toLines(parsed.slice(0, markerIndex), 0)
  return {
    phone,
    whatsapp: [
      ...sentDuringCall(phone),
      ...toLines(parsed.slice(markerIndex + 1), markerIndex + 1),
    ],
  }
}
